// src/pages/Home.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useData } from "../context/DataContext";
import ProductCard from "../components/ProductCard";
import "./Home.css";

const HIGHLIGHTS = [
  { icon: "🌎", label: "100% Sustainable Products" },
  { icon: "🌱", label: "100% Organic & Chemical-Free" },
  { icon: "📈", label: "Innovative Solutions" },
  { icon: "🏡", label: "Premium Lifestyle" },
];

export default function Home() {
  const { products, niches, loading } = useData();
  const [activeNiche, setActiveNiche] = useState(null);
  const featuredProducts = products.filter((p) => p.featured && !p.comingSoon);
  const filteredProducts = activeNiche
    ? products.filter((p) => p.niches?.includes(activeNiche) && !p.comingSoon)
    : featuredProducts;

  return (
    <div className="home">
      {/* ─── HERO ─────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero__bg">
          <div className="hero__leaf hero__leaf--1">🍃</div>
          <div className="hero__leaf hero__leaf--2">🌿</div>
          <div className="hero__leaf hero__leaf--3">🍃</div>
          <div className="hero__orb hero__orb--1" />
          <div className="hero__orb hero__orb--2" />
        </div>

        <div className="hero__content container">
          <div className="hero__text">
            <div className="hero__eyebrow">
              <span className="badge">✨ Luxury Sustainable Products & Services</span>
            </div>
            <h1 className="hero__title">
              Sunaharco
              <br />
              <em>Crafted for Every Choice</em>
            </h1>
            <p className="hero__subtitle">
              Two Paths. One Purpose. Sustainable Living & Everyday Elegance.
              From eco-friendly agricultural solutions to premium lifestyle
              products, Sunaharco brings thoughtfully crafted offerings for a
              better tomorrow.
            </p>

            <div className="hero__highlights">
              {HIGHLIGHTS.map((h) => (
                <div key={h.label} className="hero__highlight">
                  <span>{h.icon}</span>
                  <span>{h.label}</span>
                </div>
              ))}
            </div>

            <div className="hero__actions">
              <Link to="/products" className="btn btn-primary hero__cta">
                Explore Products →
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Get in Touch
              </Link>
            </div>
          </div>

          <div className="hero__visual">
            <div className="hero__product-img-wrap animate-float">
              <img
                src="/assets/logo-without-background.png"
                alt="Urvaraa Vermicompost 5Kg"
                className="hero__product-img"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="hero__product-placeholder">
                <span>🌿</span>
                <span>Urvaraa</span>
                <span>Vermicompost</span>
              </div>
            </div>
            <div className="hero__badge-float hero__badge-float--organic">
              Sustainable Products
            </div>
            <div className="hero__badge-float hero__badge-float--soil">
              Innovative Solutions
            </div>
          </div>
        </div>
      </section>

      {/* ─── OUR NICHES ───────────────────────────────────── */}
      <section className="section niches-section">
        <div className="container">
          <div className="section-header">
            <span className="badge">🌍 Our Focus Areas</span>
            <h2 className="section-title">Many Niches, One Mission:</h2>
            <p className="section-subtitle">
              Click on a niche below to explore products crafted specifically
              for that segment.
            </p>
          </div>

          <div className="niches-grid">
            {loading
              ? [1, 2, 3].map((i) => (
                  <div key={i} className="niche-card niche-card--skeleton" />
                ))
              : niches.map((niche) => (
                  <button
                    key={niche.id}
                    className={`niche-card ${
                      activeNiche === niche.id ? "niche-card--active" : ""
                    }`}
                    style={{
                      "--niche-color": niche.color,
                      "--niche-accent": niche.accentColor,
                    }}
                    onClick={() =>
                      setActiveNiche(activeNiche === niche.id ? null : niche.id)
                    }
                  >
                    <img src={`/assets/${niche.id}.jpg`} alt={niche.name} className="niche-card__image" onError={(e) => { e.target.style.display = "none"; }} />
                    <h3 className="niche-card__name">{niche.name}</h3>
                    <p className="niche-card__tagline">{niche.tagline}</p>
                    <p className="niche-card__desc">{niche.description}</p>
                    <span className="niche-card__cta">
                      {activeNiche === niche.id
                        ? "✓ Selected"
                        : "View Products →"}
                    </span>
                  </button>
                ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURED PRODUCTS ────────────────────────────── */}
      <section className="section section-alt products-section">
        <div className="container">
          <div className="section-header">
            <span className="badge">🛒 Our Products</span>
            <h2 className="section-title">
              {activeNiche
                ? `${niches.find((n) => n.id === activeNiche)?.name} Products`
                : "Featured Products"}
            </h2>
            {activeNiche && (
              <button
                className="btn btn-outline"
                style={{ fontSize: 13, padding: "8px 18px", marginTop: 8 }}
                onClick={() => setActiveNiche(null)}
              >
                Clear Filter
              </button>
            )}
          </div>

          {loading ? (
            <div className="products-grid">
              {[1, 2].map((i) => (
                <div key={i} className="card product-skeleton" />
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="products-grid">
              {filteredProducts.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
              {/* Coming soon placeholder */}
              <ProductCard product={{ comingSoon: true }} />
            </div>
          ) : (
            <div className="products-empty">
              <span>🌱</span>
              <p>No products found for this category yet. Check back soon!</p>
              <button
                className="btn btn-outline"
                onClick={() => setActiveNiche(null)}
              >
                Show All Products
              </button>
            </div>
          )}

          <div className="products-cta">
            <Link to="/products" className="btn btn-primary">
              View All Products →
            </Link>
          </div>
        </div>
      </section>

      {/* ─── ABOUT SNAPSHOT ───────────────────────────────── */}
      <section className="section about-snap">
        <div className="container">
          <div className="about-snap__inner">
            <div className="about-snap__text">
              <span className="badge">🌿 Who We Are</span>
              <h2 className="section-title">
                Nurturing Nature Through Sustainable Solutions
              </h2>
              <p>
                At Sunaharco, we believe in nurturing nature through sustainable
                and eco-friendly agricultural solutions. Our products are
                crafted to restore soil vitality and promote healthier
                growth—naturally.
              </p>
              <div className="about-snap__values">
                {["Sustainability", "Quality", "Innovation", "Trust"].map(
                  (v) => (
                    <div key={v} className="about-snap__value">
                      <span className="about-snap__value-dot" />
                      {v}
                    </div>
                  )
                )}
              </div>
              <Link
                to="/about"
                className="btn btn-primary"
                style={{ marginTop: 8 }}
              >
                Read Our Story →
              </Link>
            </div>
            <div className="about-snap__image">
              <div className="about-snap__img-bg" />
              <div className="about-snap__stats">
                <div className="stat">
                  <span className="stat__num">100%</span>
                  <span className="stat__label">Organic</span>
                </div>
                <div className="stat">
                  <span className="stat__num">3+</span>
                  <span className="stat__label">Use Cases</span>
                </div>
                <div className="stat">
                  <span className="stat__num">∞</span>
                  <span className="stat__label">Growth Potential</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CALL TO ACTION ───────────────────────────────── */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-inner">
            <h2>🌱 Grow Naturally with Sunaharco</h2>
            <p>
              Ready to transform your soil and grow healthier plants? Let's
              connect.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn btn-gold">
                Contact Us
              </Link>
              <Link
                to="/products"
                className="btn btn-outline"
                style={{ borderColor: "white", color: "white" }}
              >
                Explore Products
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
